import { useEffect, useState } from "react";

// Single shared ticker for ALL WeatherChips on the page (perf guardrail from
// the plan: never N timers). One setInterval increments a module-level index;
// every mounted chip subscribes and re-renders only itself on each tick. The
// interval is created lazily on first subscriber and torn down when the last
// chip unmounts, so it never runs when no chip is visible.

const CYCLE_MS = 2600; // ~2.6s per field (rain% -> temp -> wind)
const FIELD_COUNT = 3;

let tickIndex = 0;
let intervalId = null;
const listeners = new Set();

const start = () => {
  if (intervalId !== null) return;
  intervalId = setInterval(() => {
    tickIndex = (tickIndex + 1) % FIELD_COUNT;
    listeners.forEach((fn) => fn(tickIndex));
  }, CYCLE_MS);
};

const stop = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

/**
 * Returns the current cycle index (0..2). Subscribes this component to the
 * single shared ticker. `paused` (e.g. on hover, where the popover shows all
 * fields) keeps the index frozen for this chip without stopping the global
 * ticker for the others.
 */
export const useWeatherCycle = (paused = false) => {
  const [idx, setIdx] = useState(tickIndex);

  useEffect(() => {
    if (paused) return undefined;
    const listener = (next) => setIdx(next);
    listeners.add(listener);
    start();
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) stop();
    };
  }, [paused]);

  // When paused, idx stops advancing (no subscription) and stays frozen; the
  // current value is still returned so the chip keeps showing the last field.
  return idx;
};

export const WEATHER_FIELD_COUNT = FIELD_COUNT;
